import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatMoney, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, X, Check } from "lucide-react";

export default function Agent() {
  const { user, loading: authLoading } = useAuth();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/login?next=/agent"); return; }
    if (user.role !== "local_agent" && user.role !== "admin") {
      toast.error("Agent access required"); nav("/dashboard"); return;
    }
    refresh();
  }, [user, authLoading, nav]);

  const refresh = () => {
    setLoading(true);
    api.get("/agent/verifications").then(r => setQueue(r.data)).finally(() => setLoading(false));
  };

  const decide = async (verification_id, decision) => {
    try {
      await api.post(`/agent/verifications/${verification_id}/decide`, null, { params: { decision } });
      toast.success(`Part ${decision}`);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (authLoading || loading) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-[#009A44]" size={28} /></div>;

  return (
    <main className="max-w-[1400px] mx-auto px-4 lg:px-8 py-12" data-testid="agent-page">
      <div className="mb-8">
        <div className="text-[11px] font-bold uppercase tracking-widest text-[#FFD100] mb-2">Local Agent</div>
        <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight">Verification queue</h1>
        <p className="text-zinc-500 mt-2 text-sm">
          Physically inspect each part and approve only authentic items. Your diligence powers trust across the platform.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <Stat label="Pending inspection" value={queue.length} color="#FFD100" />
        <Stat label="Your role" value={user.role} color="#009A44" />
        <Stat label="Region" value={user.country || "Global"} color="#121212" />
      </div>

      {queue.length === 0 ? (
        <div className="bg-white border border-[#E4E4E7] p-12 text-center">
          <ShieldCheck size={48} className="mx-auto mb-3 text-[#009A44]" />
          <h2 className="font-display text-2xl font-bold mb-2">All clear!</h2>
          <p className="text-zinc-500">No parts awaiting verification right now.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="verification-queue">
          {queue.map(v => (
            <div key={v.verification_id} className="bg-white border border-[#E4E4E7] p-5" data-testid={`verification-${v.verification_id}`}>
              {v.part && (
                <div className="flex gap-3 mb-4">
                  <img src={v.part.image_url} alt="" className="w-24 h-24 object-cover bg-zinc-100" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{v.part.category} · {v.part.brand}</div>
                    <div className="font-semibold line-clamp-2">{v.part.name}</div>
                    <div className="text-xs text-zinc-500 mt-1 font-mono">PN: {v.part.part_number}</div>
                    <div className="font-display font-extrabold text-lg mt-1">{formatMoney(v.part.price, v.part.currency)}</div>
                  </div>
                </div>
              )}
              <div className="text-xs text-zinc-500 mb-4">Submitted: {new Date(v.created_at).toLocaleString()}</div>
              <div className="flex gap-2">
                <button onClick={() => decide(v.verification_id, "rejected")} className="flex-1 border-2 border-[#EF3340] text-[#EF3340] hover:bg-[#EF3340] hover:text-white py-2 text-sm font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1" data-testid={`reject-${v.verification_id}`}>
                  <X size={14} /> Reject
                </button>
                <button onClick={() => decide(v.verification_id, "approved")} className="flex-1 bg-[#009A44] hover:bg-[#007A33] text-white py-2 text-sm font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1" data-testid={`approve-${v.verification_id}`}>
                  <Check size={14} /> Approve
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}

function Stat({ label, value, color = "#52525B" }) {
  return (
    <div className="bg-white border border-[#E4E4E7] p-5">
      <div className="text-[11px] uppercase tracking-widest font-bold mb-1" style={{ color }}>{label}</div>
      <div className="font-display text-3xl font-extrabold capitalize">{value}</div>
    </div>
  );
}
